/**
 * ASSIST-05 — what the Purchase Room knows, read from get_purchase_room, and
 * every rule it applies to show it.
 *
 * The server decides who may see a case (`assist_case_gate`) and what each
 * role sees (an undisclosed share, an unchecked payee's account and the
 * advisor-only events never reach the buyer). This module only reads the
 * JSON and derives what to show from it: steps, chapters and fees come from
 * the case's own rows, never from a fixed list (a late `agreed` answer removes
 * the buyer_agreement step, for example).
 *
 * Imports only import-free modules, so `tsx` tests load it directly.
 */

import { formatListingPrice } from '../../../utils/formatCurrency';

// ---------------------------------------------------------------------------
// The data (DATA_DICTIONARY.md, get_purchase_room)
// ---------------------------------------------------------------------------

export type ViewerRole = 'buyer' | 'advisor' | 'partner';
export type CaseStatus = 'active' | 'stopped' | 'withdrawn' | 'completed' | 'closed';
export type StepStatus = 'not_started' | 'in_progress' | 'waiting' | 'done' | 'stopped';
export type WaitingOn =
  | 'buyer'
  | 'advisor'
  | 'seller'
  | 'sellers_agent'
  | 'advocate'
  | 'management_company'
  | 'valuer'
  | 'surveyor'
  | 'lands'
  | 'bank';
export type OwnerRole = 'buyer' | 'advisor' | 'advocate' | 'valuer';
export type EvidenceKind = 'document' | 'note' | 'document_or_note';
export type DocumentStatus = 'pending_upload' | 'requested' | 'uploaded' | 'accepted' | 'rejected';
export type DocumentCategory = 'identity' | 'kyc' | 'property' | 'legal' | 'payment' | 'viewing' | 'other';
export type CommissionAnswer = 'agreed' | 'refused' | 'no_answer';

export interface RoomCase {
  id: string;
  chapter: number;
  status: CaseStatus;
  stopReason: string | null;
  withdrawReason: string | null;
  titleType: string | null;
  agreedPriceKes: number | null;
  agreedAt: string | null;
  registeredPriceKes: number | null;
  advocateFirmName: string | null;
  valuerName: string | null;
  isTest: boolean;
  createdAt: string;
  updatedAt: string | null;
  endedAt: string | null;
}

export interface RoomListing {
  source: 'listing' | 'syndicated';
  listingId: string | null;
  syndicatedId: string | null;
  title: string | null;
  slug: string | null;
  propertyType: string | null;
  agencyName: string | null;
  city: string | null;
  neighborhood: string | null;
  askingPrice: number | null;
  currency: string | null;
  imageUrl: string | null;
  photoCredit: string | null;
  /** False once the listing is sold, unpublished or deleted: snapshot only. */
  listingLive: boolean;
}

export interface RoomAdvisor {
  displayName: string;
  earbRegistrationNo: string | null;
  practisingYear: number | null;
}

export interface RoomTerms {
  profile: 'live' | 'test' | null;
  feeCapPct: number | null;
  buyerRatePct: number | null;
  buyerFloorKes: number | null;
  responseWindowText: string | null;
  noAnswerCountsAsRefused: boolean | null;
}

export interface RoomStep {
  id: string;
  chapter: number;
  sortOrder: number;
  stepKey: string;
  title: string;
  ownerRole: OwnerRole;
  evidenceKind: EvidenceKind;
  buyerDecision: boolean;
  status: StepStatus;
  waitingOn: WaitingOn | null;
  waitingSince: string | null;
  dueAt: string | null;
  doneAt: string | null;
  verifiedByName: string | null;
  evidenceDocumentId: string | null;
  evidenceNote: string | null;
}

export interface RoomDocument {
  id: string;
  stepKey: string | null;
  category: DocumentCategory;
  label: string;
  status: DocumentStatus;
  rejectReason: string | null;
  uploadedAt: string | null;
  createdAt: string;
}

export interface RoomPayment {
  id: string;
  kind: string;
  payeeName: string | null;
  payeeType: string | null;
  verified: boolean;
  verifiedAt: string | null;
  /** Null for the buyer until the payee's phone check (AC-05). */
  accountMasked: string | null;
  estimateKes: number | null;
  paidKes: number | null;
  paidAt: string | null;
}

export interface RoomFee {
  payer: 'seller_side' | 'buyer';
  ratePct: number | null;
  amountKes: number | null;
  status: 'not_due' | 'invoiced' | 'paid';
  dueAt: string | null;
  paidAt: string | null;
}

export interface RoomCommission {
  agencyName: string | null;
  askedAt: string | null;
  replyDueAt: string | null;
  answeredAt: string | null;
  /** Null for the buyer while an `agreed` answer is undisclosed (R9). */
  answer: CommissionAnswer | null;
  sharePct: number | null;
  disclosedToBuyerAt: string | null;
}

export interface RoomAgreement {
  kind: 'representation_letter' | 'buyer_agreement';
  feeCapPct: number | null;
  buyerRatePct: number | null;
  buyerFloorKes: number | null;
  signedAt: string | null;
}

export interface RoomEvent {
  id: string;
  action: string;
  actorRole: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  at: string;
}

export interface PurchaseRoom {
  viewerRole: ViewerRole;
  case: RoomCase;
  listing: RoomListing;
  buyerFullName: string | null;
  advisor: RoomAdvisor | null;
  terms: RoomTerms;
  steps: RoomStep[];
  documents: RoomDocument[];
  payments: RoomPayment[];
  fee: RoomFee | null;
  commission: RoomCommission | null;
  agreements: RoomAgreement[];
  events: RoomEvent[];
}

/** One open case from get_my_active_case() (the nav and the account row). */
export interface ActiveCase {
  caseId: string;
  title: string | null;
  chapter: number;
  status: 'active' | 'stopped';
  isTest: boolean;
}

// ---------------------------------------------------------------------------
// Reading the JSON. Never throws; anything unusable is null (→ NotFound).
// ---------------------------------------------------------------------------

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A case id the room may ask for. Anything else is NotFound without a request. */
export function isCaseId(value: unknown): value is string {
  return typeof value === 'string' && UUID.test(value);
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};
const bool = (v: unknown): boolean => v === true;
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | null =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : null;
const list = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : []);
const isDate = (v: string | null): v is string => !!v && !Number.isNaN(Date.parse(v));
const date = (v: unknown): string | null => {
  const s = str(v);
  return isDate(s) ? s : null;
};

const CASE_STATUSES: readonly CaseStatus[] = ['active', 'stopped', 'withdrawn', 'completed', 'closed'];
const STEP_STATUSES: readonly StepStatus[] = ['not_started', 'in_progress', 'waiting', 'done', 'stopped'];
const PARTIES: readonly WaitingOn[] = [
  'buyer',
  'advisor',
  'seller',
  'sellers_agent',
  'advocate',
  'management_company',
  'valuer',
  'surveyor',
  'lands',
  'bank',
];
const OWNERS: readonly OwnerRole[] = ['buyer', 'advisor', 'advocate', 'valuer'];
const EVIDENCE: readonly EvidenceKind[] = ['document', 'note', 'document_or_note'];
const DOC_STATUSES: readonly DocumentStatus[] = ['pending_upload', 'requested', 'uploaded', 'accepted', 'rejected'];
const CATEGORIES: readonly DocumentCategory[] = ['identity', 'kyc', 'property', 'legal', 'payment', 'viewing', 'other'];

function parseStep(s: Obj): RoomStep | null {
  const id = str(s.id);
  const stepKey = str(s.step_key);
  const title = str(s.title);
  const chapter = num(s.chapter);
  const status = oneOf(s.status, STEP_STATUSES);
  if (!id || !stepKey || !title || !chapter || chapter < 1 || chapter > 7 || !status) return null;
  return {
    id,
    chapter,
    sortOrder: num(s.sort_order) ?? 0,
    stepKey,
    title,
    ownerRole: oneOf(s.owner_role, OWNERS) ?? 'advisor',
    evidenceKind: oneOf(s.evidence_kind, EVIDENCE) ?? 'document',
    buyerDecision: bool(s.buyer_decision),
    status,
    waitingOn: oneOf(s.waiting_on, PARTIES),
    waitingSince: date(s.waiting_since),
    dueAt: date(s.due_at),
    doneAt: date(s.done_at),
    verifiedByName: str(s.verified_by_name),
    evidenceDocumentId: str(s.evidence_document_id),
    evidenceNote: str(s.evidence_note),
  };
}

function parseDocument(d: Obj): RoomDocument | null {
  const id = str(d.id);
  const status = oneOf(d.status, DOC_STATUSES);
  const category = oneOf(d.category, CATEGORIES);
  const label = str(d.label);
  if (!id || !status || !category || !label) return null;
  return {
    id,
    stepKey: str(d.step_key),
    category,
    label,
    status,
    rejectReason: str(d.reject_reason),
    uploadedAt: date(d.uploaded_at),
    createdAt: date(d.created_at) ?? '',
  };
}

function parsePayment(p: Obj): RoomPayment | null {
  const id = str(p.id);
  const kind = str(p.kind);
  if (!id || !kind) return null;
  const verifiedAt = date(p.verified_at);
  const verified = p.verified === true && !!verifiedAt;
  return {
    id,
    kind,
    payeeName: str(p.payee_name),
    payeeType: str(p.payee_type),
    verified,
    verifiedAt: verified ? verifiedAt : null,
    // Belt and braces: the server hides it until the check; never show it before.
    accountMasked: verified ? str(p.account_masked) : null,
    estimateKes: num(p.estimate_kes),
    paidKes: num(p.paid_kes),
    paidAt: date(p.paid_at),
  };
}

function parseEvent(e: Obj): RoomEvent | null {
  const id = str(e.id);
  const action = str(e.action);
  const at = date(e.at);
  if (!id || !action || !at) return null;
  return {
    id,
    action,
    actorRole: str(e.actor_role),
    before: isObj(e.before) ? e.before : null,
    after: isObj(e.after) ? e.after : null,
    at,
  };
}

const notNull = <T,>(v: T | null): v is T => v !== null;

export function parsePurchaseRoom(raw: unknown): PurchaseRoom | null {
  if (!isObj(raw)) return null;
  const viewerRole = oneOf(raw.viewer_role, ['buyer', 'advisor', 'partner'] as const);
  const c = raw.case;
  const l = raw.listing;
  if (!viewerRole || !isObj(c) || !isObj(l)) return null;

  const id = str(c.id);
  const chapter = num(c.chapter);
  const status = oneOf(c.status, CASE_STATUSES);
  const createdAt = date(c.created_at);
  if (!isCaseId(id) || !chapter || chapter < 1 || chapter > 7 || !status || !createdAt) return null;

  const a = raw.advisor;
  const t = isObj(raw.terms) ? raw.terms : {};
  const f = raw.fee;
  const cr = raw.commission;

  return {
    viewerRole,
    case: {
      id,
      chapter: Math.trunc(chapter),
      status,
      stopReason: str(c.stop_reason),
      withdrawReason: str(c.withdraw_reason),
      titleType: str(c.title_type),
      agreedPriceKes: num(c.agreed_price_kes),
      agreedAt: date(c.agreed_at),
      registeredPriceKes: num(c.registered_price_kes),
      advocateFirmName: str(c.advocate_firm_name),
      valuerName: str(c.valuer_name),
      isTest: bool(c.is_test),
      createdAt,
      updatedAt: date(c.updated_at),
      endedAt: date(c.ended_at),
    },
    listing: {
      source: oneOf(l.source, ['listing', 'syndicated'] as const) ?? 'listing',
      listingId: str(l.listing_id),
      syndicatedId: str(l.syndicated_id),
      title: str(l.title),
      slug: str(l.slug),
      propertyType: str(l.property_type),
      agencyName: str(l.agency_name),
      city: str(l.city),
      neighborhood: str(l.neighborhood),
      askingPrice: num(l.asking_price),
      currency: str(l.currency),
      imageUrl: str(l.image_url),
      photoCredit: str(l.photo_credit),
      listingLive: l.listing_live === true,
    },
    buyerFullName: isObj(raw.buyer) ? str(raw.buyer.full_name) : null,
    advisor:
      isObj(a) && str(a.display_name)
        ? {
            displayName: str(a.display_name) as string,
            earbRegistrationNo: str(a.earb_registration_no),
            practisingYear: num(a.practising_year),
          }
        : null,
    terms: {
      profile: oneOf(t.profile, ['live', 'test'] as const),
      feeCapPct: num(t.fee_cap_pct),
      buyerRatePct: num(t.buyer_rate_pct),
      buyerFloorKes: num(t.buyer_floor_kes),
      responseWindowText: str(t.response_window_text),
      noAnswerCountsAsRefused: typeof t.no_answer_counts_as_refused === 'boolean' ? t.no_answer_counts_as_refused : null,
    },
    steps: list(raw.steps)
      .map(parseStep)
      .filter(notNull)
      .sort((x, y) => x.chapter - y.chapter || x.sortOrder - y.sortOrder),
    documents: list(raw.documents).map(parseDocument).filter(notNull),
    payments: list(raw.payments).map(parsePayment).filter(notNull),
    fee: isObj(f)
      ? {
          payer: oneOf(f.payer, ['seller_side', 'buyer'] as const) ?? 'buyer',
          ratePct: num(f.rate_pct),
          amountKes: num(f.amount_kes),
          status: oneOf(f.status, ['not_due', 'invoiced', 'paid'] as const) ?? 'not_due',
          dueAt: date(f.due_at),
          paidAt: date(f.paid_at),
        }
      : null,
    commission: isObj(cr)
      ? {
          agencyName: str(cr.agency_name),
          askedAt: date(cr.asked_at),
          replyDueAt: date(cr.reply_due_at),
          answeredAt: date(cr.answered_at),
          answer: oneOf(cr.answer, ['agreed', 'refused', 'no_answer'] as const),
          sharePct: num(cr.share_pct),
          disclosedToBuyerAt: date(cr.disclosed_to_buyer_at),
        }
      : null,
    agreements: list(raw.agreements)
      .map((g): RoomAgreement | null => {
        const kind = oneOf(g.kind, ['representation_letter', 'buyer_agreement'] as const);
        if (!kind) return null;
        return {
          kind,
          feeCapPct: num(g.fee_cap_pct),
          buyerRatePct: num(g.buyer_rate_pct),
          buyerFloorKes: num(g.buyer_floor_kes),
          signedAt: date(g.signed_at),
        };
      })
      .filter(notNull),
    events: list(raw.events).map(parseEvent).filter(notNull),
  };
}

/** get_my_active_case(): the caller's open cases as buyer, newest first. */
export function parseActiveCases(raw: unknown): ActiveCase[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObj).flatMap((r): ActiveCase[] => {
    const caseId = str(r.case_id);
    const status = oneOf(r.status, ['active', 'stopped'] as const);
    if (!isCaseId(caseId) || !status) return [];
    return [{ caseId, title: str(r.title), chapter: num(r.chapter) ?? 1, status, isTest: bool(r.is_test) }];
  });
}

// ---------------------------------------------------------------------------
// Time and numbers (R28: Africa/Nairobi, 24-hour HH:mm; KES via formatListingPrice)
// ---------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS: Record<string, string> = { Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun' };

const nairobiParts = (() => {
  let fmt: Intl.DateTimeFormat | null = null;
  return (iso: string | Date) => {
    fmt ??= new Intl.DateTimeFormat('en-US', {
      timeZone: 'Africa/Nairobi',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    const parts: Record<string, string> = {};
    for (const p of fmt.formatToParts(typeof iso === 'string' ? new Date(iso) : iso)) parts[p.type] = p.value;
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      weekday: WEEKDAYS[parts.weekday] ?? parts.weekday,
      // Some engines print midnight as 24 even with h23.
      hour: parts.hour === '24' ? '00' : parts.hour,
      minute: parts.minute,
    };
  };
})();

/** “6 Oct”, or “6 Oct 2025” outside the current year in Nairobi. */
export function formatDay(iso: string, now: Date = new Date()): string {
  const d = nairobiParts(iso);
  const base = `${d.day} ${MONTHS[d.month - 1]}`;
  return d.year === nairobiParts(now).year ? base : `${base} ${d.year}`;
}

/** “Thu 15 Oct” (the board's due and booked dates). */
export function formatWeekdayDay(iso: string, now: Date = new Date()): string {
  return `${nairobiParts(iso).weekday} ${formatDay(iso, now)}`;
}

/** “09:12”, Nairobi time, 24-hour. */
export function formatTime(iso: string): string {
  const d = nairobiParts(iso);
  return `${d.hour}:${d.minute}`;
}

/** “Thu 15 Oct, 10:00 EAT”. */
export function formatBooking(iso: string, now: Date = new Date()): string {
  return `${formatWeekdayDay(iso, now)}, ${formatTime(iso)} EAT`;
}

const nairobiDayNumber = (iso: string | Date) => {
  const d = nairobiParts(iso);
  return Math.floor(Date.UTC(d.year, d.month - 1, d.day) / 86_400_000);
};

export function isSameNairobiDay(a: string, b: Date): boolean {
  return nairobiDayNumber(a) === nairobiDayNumber(b);
}

/** Day 1 is the day the case was created, in Nairobi. */
export function dayOfPurchase(createdAt: string, now: Date = new Date()): number {
  return Math.max(1, nairobiDayNumber(now) - nairobiDayNumber(createdAt) + 1);
}

/** KES with formatListingPrice (never formatCurrency, which prints “Ksh”). */
export function kes(amount: number): string {
  return formatListingPrice(amount, 'KES');
}

/** A numeric(5,2) percentage as people write it: 2.5, 1.11, 3. */
export function formatPct(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** 6.10 “[last 4]”: the last four characters of `account_masked`. */
export function lastFour(accountMasked: string | null): string | null {
  if (!accountMasked) return null;
  const visible = accountMasked.replace(/\s+/g, '');
  return visible.length ? visible.slice(-4) : null;
}

// ---------------------------------------------------------------------------
// Who the room is talking to (R28)
// ---------------------------------------------------------------------------

/**
 * The greeting's name: the first token of `profiles.full_name`, dropped when
 * it is missing, “User”, an email address, or the local part of the viewer's
 * own email (what some OAuth sign-ups store as the name).
 */
export function greetingName(fullName: string | null, viewerEmail?: string | null): string | null {
  const name = (fullName ?? '').trim();
  if (!name || name.includes('@')) return null;
  const first = name.split(/\s+/)[0];
  if (!first || first.toLowerCase() === 'user') return null;
  const local = (viewerEmail ?? '').split('@')[0]?.trim().toLowerCase();
  if (local && name.toLowerCase() === local) return null;
  return first;
}

/** The newest change the buyer can see: the case row or a visible event. */
export function lastUpdated(room: PurchaseRoom): string {
  let latest = room.case.updatedAt ?? room.case.createdAt;
  for (const e of room.events) if (Date.parse(e.at) > Date.parse(latest)) latest = e.at;
  return latest;
}

// ---------------------------------------------------------------------------
// The state of the case
// ---------------------------------------------------------------------------

export type RoomState = 'you' | 'others' | 'stopped' | 'ended';

const byDueThenOrder = (x: RoomStep, y: RoomStep) => {
  const dx = x.dueAt ? Date.parse(x.dueAt) : Infinity;
  const dy = y.dueAt ? Date.parse(y.dueAt) : Infinity;
  return dx - dy || x.chapter - y.chapter || x.sortOrder - y.sortOrder;
};

/** Steps that wait on the buyer, soonest due first. */
export function buyerTasks(room: PurchaseRoom): RoomStep[] {
  return room.steps.filter((s) => s.status === 'waiting' && s.waitingOn === 'buyer').sort(byDueThenOrder);
}

export function roomState(room: PurchaseRoom): RoomState {
  if (room.case.status === 'stopped') return 'stopped';
  if (room.case.status !== 'active') return 'ended';
  return buyerTasks(room).length > 0 ? 'you' : 'others';
}

/** 6.10 item 1: has any purchase money moved? */
export function anyPurchaseMoneyPaid(room: PurchaseRoom): boolean {
  return room.payments.some((p) => (p.paidKes ?? 0) > 0);
}

/** A party the room waits on, with the steps and since when. */
export interface WaitingGroup {
  party: WaitingOn;
  steps: RoomStep[];
  /** The earliest `waiting_since`, or the booking date for a booked step. */
  since: string | null;
  booked: string | null;
}

const OWNER_PARTY: Record<OwnerRole, WaitingOn> = {
  buyer: 'buyer',
  advisor: 'advisor',
  advocate: 'advocate',
  valuer: 'valuer',
};

/**
 * Who else the purchase waits on: steps marked `waiting` on someone other than
 * the buyer, and booked steps (a future `due_at` on a step that is not done)
 * under their owner. Grouped by party, in the order they first appear.
 */
export function waitingOnOthers(room: PurchaseRoom, now: Date = new Date()): WaitingGroup[] {
  const groups = new Map<WaitingOn, WaitingGroup>();
  const add = (party: WaitingOn, step: RoomStep, since: string | null, booked: string | null) => {
    const g = groups.get(party) ?? { party, steps: [], since: null, booked: null };
    g.steps.push(step);
    if (since && (!g.since || Date.parse(since) < Date.parse(g.since))) g.since = since;
    if (booked && (!g.booked || Date.parse(booked) < Date.parse(g.booked))) g.booked = booked;
    groups.set(party, g);
  };
  for (const s of room.steps) {
    if (s.status === 'waiting' && s.waitingOn && s.waitingOn !== 'buyer') {
      add(s.waitingOn, s, s.waitingSince, null);
    } else if (
      (s.status === 'not_started' || s.status === 'in_progress') &&
      s.dueAt &&
      Date.parse(s.dueAt) >= now.getTime() &&
      s.ownerRole !== 'buyer'
    ) {
      add(OWNER_PARTY[s.ownerRole], s, null, s.dueAt);
    }
  }
  return [...groups.values()];
}

// ---------------------------------------------------------------------------
// Who pays us (brief 6.5)
// ---------------------------------------------------------------------------

export type FeeState =
  | { kind: 'asking' }
  | { kind: 'seller'; sharePct: number }
  | { kind: 'buyer'; via: 'refused' | 'no_answer'; signedAt: string | null };

/**
 * Asking until the seller's agent answers. The buyer never learns of an
 * `agreed` answer before it is disclosed: the server sends no answer, so it
 * reads as asking. A refusal, or no answer counted as one, is the buyer
 * agreement branch, signed or not yet.
 */
export function feeState(room: PurchaseRoom): FeeState {
  const c = room.commission;
  if (!c || !c.answer) return { kind: 'asking' };
  if (c.answer === 'agreed') {
    return c.disclosedToBuyerAt && c.sharePct !== null ? { kind: 'seller', sharePct: c.sharePct } : { kind: 'asking' };
  }
  const signedAt = room.agreements.find((g) => g.kind === 'buyer_agreement')?.signedAt ?? null;
  if (c.answer === 'refused') return { kind: 'buyer', via: 'refused', signedAt };
  // no_answer: the buyer agreement branch only when it counts as a refusal (D9).
  return room.terms.noAnswerCountsAsRefused === true || signedAt
    ? { kind: 'buyer', via: 'no_answer', signedAt }
    : { kind: 'asking' };
}

/** The cap on total pay: stamped on the signed letter, else the config row. */
export function feeCap(room: PurchaseRoom): number | null {
  const stamped = room.agreements.find((g) => g.kind === 'representation_letter' && g.signedAt)?.feeCapPct;
  return stamped ?? room.terms.feeCapPct;
}

/** The buyer's rate and floor: stamped on the signed agreement, else the config row. */
export function buyerTerms(room: PurchaseRoom): { rate: number | null; floor: number | null } {
  const signed = room.agreements.find((g) => g.kind === 'buyer_agreement' && g.signedAt);
  if (signed) return { rate: signed.buyerRatePct, floor: signed.buyerFloorKes };
  return { rate: room.terms.buyerRatePct, floor: room.terms.buyerFloorKes };
}

// ---------------------------------------------------------------------------
// Chapters and checks
// ---------------------------------------------------------------------------

export function chapterSteps(room: PurchaseRoom, chapter: number): RoomStep[] {
  return room.steps.filter((s) => s.chapter === chapter);
}

export function checksCount(room: PurchaseRoom, chapter: number): { done: number; total: number } {
  const steps = chapterSteps(room, chapter);
  return { done: steps.filter((s) => s.status === 'done').length, total: steps.length };
}

export interface JourneyNode {
  chapter: number;
  state: 'done' | 'current' | 'future';
  /** When a done chapter finished (the `chapter_advanced` event into the next one). */
  finishedAt: string | null;
}

/** When the case moved into `chapter` (created_at for chapter 1). */
export function chapterStartedAt(room: PurchaseRoom, chapter: number): string | null {
  if (chapter === 1) return room.case.createdAt;
  const e = room.events.find((ev) => ev.action === 'chapter_advanced' && num(ev.after?.chapter) === chapter);
  return e?.at ?? null;
}

export function journey(room: PurchaseRoom): JourneyNode[] {
  return [1, 2, 3, 4, 5, 6, 7].map((chapter) => ({
    chapter,
    state: chapter < room.case.chapter ? 'done' : chapter === room.case.chapter ? 'current' : 'future',
    finishedAt: chapter < room.case.chapter ? chapterStartedAt(room, chapter + 1) : null,
  }));
}

// ---------------------------------------------------------------------------
// Done for you so far: only items with evidence
// ---------------------------------------------------------------------------

export type DoneItem =
  | { kind: 'price'; asking: number; agreed: number; agreedAt: string }
  | { kind: 'fee'; disclosedAt: string }
  | { kind: 'step'; step: RoomStep };

/**
 * What the advisor has done, newest first: the negotiated price (once agreed
 * below asking), the seller's side paying our fee (once disclosed), and every
 * advisor step marked done, which by invariant 1 has its evidence, a named
 * verifier and a time.
 */
export function doneForYou(room: PurchaseRoom): DoneItem[] {
  const items: { at: number; item: DoneItem }[] = [];
  const asking = room.listing.askingPrice;
  const agreed = room.case.agreedPriceKes;
  const sameCurrency = !room.listing.currency || room.listing.currency.toUpperCase() === 'KES';
  if (asking !== null && agreed !== null && room.case.agreedAt && sameCurrency && agreed < asking) {
    items.push({ at: Date.parse(room.case.agreedAt), item: { kind: 'price', asking, agreed, agreedAt: room.case.agreedAt } });
  }
  const fee = feeState(room);
  if (fee.kind === 'seller' && room.commission?.disclosedToBuyerAt) {
    const at = room.commission.disclosedToBuyerAt;
    items.push({ at: Date.parse(at), item: { kind: 'fee', disclosedAt: at } });
  }
  for (const s of room.steps) {
    if (s.ownerRole === 'advisor' && s.status === 'done' && s.doneAt && s.verifiedByName) {
      items.push({ at: Date.parse(s.doneAt), item: { kind: 'step', step: s } });
    }
  }
  return items.sort((x, y) => y.at - x.at).map((x) => x.item);
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export type DocumentGroup = 'identity' | 'property' | 'legal' | 'payment' | 'other';
const GROUP_OF: Record<DocumentCategory, DocumentGroup> = {
  identity: 'identity',
  kyc: 'identity',
  property: 'property',
  viewing: 'property',
  legal: 'legal',
  payment: 'payment',
  other: 'other',
};
const GROUP_ORDER: readonly DocumentGroup[] = ['identity', 'property', 'legal', 'payment', 'other'];

/** Statuses `assist-documents` `link` will open. */
export function isOpenable(doc: RoomDocument): boolean {
  return doc.status === 'uploaded' || doc.status === 'accepted' || doc.status === 'rejected';
}

/** The room's documents, grouped as the board does. An unfinished upload is not shown. */
export function documentGroups(room: PurchaseRoom): { group: DocumentGroup; documents: RoomDocument[] }[] {
  const shown = room.documents.filter((d) => d.status !== 'pending_upload');
  return GROUP_ORDER.map((group) => ({ group, documents: shown.filter((d) => GROUP_OF[d.category] === group) })).filter(
    (g) => g.documents.length > 0,
  );
}

export function documentById(room: PurchaseRoom, id: string | null): RoomDocument | null {
  if (!id) return null;
  return room.documents.find((d) => d.id === id) ?? null;
}

// ---------------------------------------------------------------------------
// The last visit (TOKENS.md: motion only for what changed since)
// ---------------------------------------------------------------------------

export interface SeenState {
  chapter: number;
  yourMove: string | null;
  fee: FeeState['kind'];
}

export function seenStateOf(room: PurchaseRoom): SeenState {
  return { chapter: room.case.chapter, yourMove: buyerTasks(room)[0]?.stepKey ?? null, fee: feeState(room).kind };
}

/** What to animate on this visit. A first visit counts as new. */
export function motionSince(previous: SeenState | null, now: SeenState) {
  return {
    chapter: !previous || now.chapter > previous.chapter,
    yourMove: !!now.yourMove && (!previous || previous.yourMove !== now.yourMove),
    fee: !previous || previous.fee !== now.fee,
  };
}

export function parseSeenState(raw: string | null): SeenState | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Obj;
    const chapter = num(v.chapter);
    const fee = oneOf(v.fee, ['asking', 'seller', 'buyer'] as const);
    if (!chapter || !fee) return null;
    return { chapter, yourMove: str(v.yourMove), fee };
  } catch {
    return null;
  }
}
